export type YYYYMMDD = `${number}${number}${number}${number}-${number}${number}-${number}${number}`;

export type ElapsedTime = {
    value: number;
    unit:
        | 'years'
        | 'year'
        | 'months'
        | 'month'
        | 'days'
        | 'day'
        | 'hours'
        | 'hour'
        | 'minutes'
        | 'minute'
        | 'seconds'
        | 'second';
};

export function isYYYMMDD(date: string): date is YYYYMMDD {
    if (!date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return false;
    }
    return new Date(date) instanceof Date;
}

export function formatDateToYYYYMMDD(date: Date): YYYYMMDD {
    const year = date.getFullYear() as unknown as `${number}${number}${number}${number}`;
    const month = (date.getMonth() + 1).toString().padStart(2, '0') as `${number}${number}`;
    const day = date.getDate().toString().padStart(2, '0') as `${number}${number}`;

    return `${year}-${month}-${day}`;
}

export function convertDateToUTC(date: Date) {
    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
    );
}

export function getDateFromEUDateStringFormat(date: string) {
    const [day, month, year] = date.split('/');
    return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * Builds a local Date from a `yyyy-mm-dd` string without timezone shift.
 * Avoids +/- 1 day displayed (e.g. 2021/11/15 instead of 2021/11/16 for people in Canada).
 */
export function getDisplayableDate(date: string): Date {
    const matches = date.match(/([0-9]{4})-([0-9]{2})-([0-9]{2})/);

    if (!matches) {
        throw new Error(`Wrong date format "${date}", it should be yyyy-mm-dd`);
    }

    const year = parseInt(matches[1], 10);
    const month = parseInt(matches[2], 10) - 1;
    const day = parseInt(matches[3], 10);

    return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Formats an elapsed duration from a given date until another date.
 * Returns an object { value: number, unit: string } according to granularity.
 * - Years if > 12 months
 * - Months if > 30 days
 * - Days if > 1 day
 * - Hours if > 1 hour
 * - Minutes if > 1 minute
 * - Seconds otherwise
 */
export function formatElapsedTime(
    since: Date | string,
    to: Date | string = new Date(),
): ElapsedTime {
    const start = typeof since === 'string' ? new Date(since) : since;
    const end = typeof to === 'string' ? new Date(to) : to;

    if (start > end) {
        return { value: 0, unit: 'seconds' };
    }

    const diff = Math.floor((end.getTime() - start.getTime()) / 1000);
    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(diff / 3600);
    const days = Math.floor(diff / 86400);
    const months = Math.floor(diff / 2592000);
    const years = Math.floor(diff / 31536000);

    if (years > 1) {
        return { value: years, unit: 'years' };
    }

    if (years === 1) {
        return { value: 1, unit: 'year' };
    }

    if (months > 1) {
        return { value: months, unit: 'months' };
    }

    if (months === 1) {
        return { value: 1, unit: 'month' };
    }

    if (days >= 1) {
        return { value: days, unit: 'days' };
    }

    if (days === 1) {
        return { value: 1, unit: 'day' };
    }

    if (hours > 1) {
        return { value: hours, unit: 'hours' };
    }

    if (hours === 1) {
        return { value: 1, unit: 'hour' };
    }

    if (minutes > 1) {
        return { value: minutes, unit: 'minutes' };
    }

    if (minutes === 1) {
        return { value: 1, unit: 'minute' };
    }

    if (diff > 1) {
        return { value: diff, unit: 'seconds' };
    }

    return { value: diff, unit: 'second' };
}
